import React, { useState, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  AttachFile as AttachFileIcon,
} from '@mui/icons-material';
import { gekkoApi } from '../../api/GekkoApi';

export interface UploadedAttachment {
  id: string;
  filename: string;
  size: number;
  status: 'uploading' | 'uploaded' | 'error';
  error?: string;
}

interface AttachmentUploadProps {
  attachments: UploadedAttachment[];
  onAttachmentsChange: (attachments: UploadedAttachment[]) => void;
  disabled?: boolean;
  maxFiles?: number;
  acceptedTypes?: string[];
}

export const AttachmentUpload: React.FC<AttachmentUploadProps> = ({
  attachments,
  onAttachmentsChange,
  disabled = false,
  maxFiles = 10,
  acceptedTypes = ['.pdf', '.jpg', '.jpeg', '.png', '.txt', '.doc', '.docx'],
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Get presigned upload URL for attachment
  const getUploadUrl = async (filename: string, contentType: string) => {
    const response = await gekkoApi.post<{
      id: string;
      upload_url: string;
    }>('/cases/attachments', {
      body: {
        filename,
        mime_type: contentType,
      },
    });
    return response;
  };

  // Upload file to presigned URL
  const uploadFileToS3 = async (file: File, uploadUrl: string) => {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.statusText}`);
    }
  };

  const handleFileUpload = useCallback(
    async (files: FileList) => {
      if (disabled) return;

      const newAttachments: UploadedAttachment[] = Array.from(files).map(
        (file) => ({
          id: '', // Will be set after getting upload URL
          filename: file.name,
          size: file.size,
          status: 'uploading' as const,
        })
      );

      // Add new attachments to the list immediately with uploading status
      const updatedAttachments = [...attachments, ...newAttachments];
      onAttachmentsChange(updatedAttachments);

      // Process each file
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const attachmentIndex = attachments.length + i;

        try {
          // Get presigned upload URL
          const { id, upload_url } = await getUploadUrl(file.name, file.type);

          // Update attachment with ID
          const currentAttachments = [...updatedAttachments];
          currentAttachments[attachmentIndex] = {
            ...currentAttachments[attachmentIndex],
            id,
          };
          onAttachmentsChange(currentAttachments);

          // Upload file to S3
          await uploadFileToS3(file, upload_url);

          // Mark as uploaded
          const finalAttachments = [...currentAttachments];
          finalAttachments[attachmentIndex] = {
            ...finalAttachments[attachmentIndex],
            status: 'uploaded',
          };
          onAttachmentsChange(finalAttachments);
        } catch (error) {
          console.error('Error uploading file:', error);

          // Mark as error
          const errorAttachments = [...updatedAttachments];
          errorAttachments[attachmentIndex] = {
            ...errorAttachments[attachmentIndex],
            status: 'error',
            error: error instanceof Error ? error.message : 'Upload failed',
          };
          onAttachmentsChange(errorAttachments);
        }
      }
    },
    [attachments, onAttachmentsChange, disabled]
  );

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      handleFileUpload(files);
    }
    // Clear the input so the same file can be uploaded again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    if (!disabled) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);

    if (disabled) return;

    const files = event.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files);
    }
  };

  const handleRemoveAttachment = (index: number) => {
    const updatedAttachments = attachments.filter((_, i) => i !== index);
    onAttachmentsChange(updatedAttachments);
  };

  const getStatusColor = (status: UploadedAttachment['status']) => {
    switch (status) {
      case 'uploaded':
        return 'success';
      case 'uploading':
        return 'info';
      case 'error':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: UploadedAttachment['status']) => {
    switch (status) {
      case 'uploaded':
        return 'Uploaded';
      case 'uploading':
        return 'Uploading...';
      case 'error':
        return 'Error';
      default:
        return 'Unknown';
    }
  };

  const canUploadMore = attachments.length < maxFiles;

  return (
    <Box>
      {/* Upload Area */}
      {canUploadMore && (
        <Box
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            border: '2px dashed',
            borderColor: isDragOver
              ? 'primary.main'
              : disabled
              ? 'grey.400'
              : 'grey.500',
            borderRadius: 2,
            p: 3,
            textAlign: 'center',
            bgcolor: isDragOver ? 'primary.50' : 'background.paper',
            cursor: disabled ? 'not-allowed' : 'pointer',
            transition: 'all 0.2s ease',
            mb: 2,
            opacity: disabled ? 0.6 : 1,
          }}
          onClick={() => !disabled && fileInputRef.current?.click()}
        >
          <CloudUploadIcon
            sx={{
              fontSize: 48,
              color: disabled ? 'grey.400' : 'grey.600',
              mb: 1,
            }}
          />
          <Typography variant="h6" gutterBottom>
            {isDragOver ? 'Drop files here' : 'Upload Attachments'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Drag and drop files here, or click to select files
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: 'block' }}
          >
            Supported formats: {acceptedTypes.join(', ')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Maximum {maxFiles} files
          </Typography>

          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            accept={acceptedTypes.join(',')}
            onChange={handleFileInputChange}
            disabled={disabled}
          />
        </Box>
      )}

      {/* Attachments List */}
      {attachments.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Attachments ({attachments.length})
          </Typography>
          <List dense>
            {attachments.map((attachment, index) => (
              <ListItem
                key={`${attachment.filename}-${index}`}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 1,
                }}
              >
                <AttachFileIcon sx={{ mr: 1, color: 'text.secondary' }} />
                <ListItemText
                  primary={attachment.filename}
                  secondary={
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginTop: '4px',
                      }}
                    >
                      <Typography variant="caption">
                        {(attachment.size / 1024).toFixed(2)} KB
                      </Typography>
                      <Chip
                        label={getStatusLabel(attachment.status)}
                        size="small"
                        color={getStatusColor(attachment.status) as any}
                      />
                      {attachment.status === 'uploading' && (
                        <CircularProgress size={16} />
                      )}
                    </span>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={() => handleRemoveAttachment(index)}
                    disabled={attachment.status === 'uploading'}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>

          {/* Show error message if any uploads failed */}
          {attachments.some((a) => a.status === 'error') && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Some files failed to upload. Please try uploading them again.
            </Alert>
          )}
        </Box>
      )}

      {/* Max files reached message */}
      {!canUploadMore && (
        <Alert severity="info" sx={{ mt: 1 }}>
          Maximum number of files ({maxFiles}) reached.
        </Alert>
      )}
    </Box>
  );
};

export default AttachmentUpload;
